import React, { useState } from "react";
import { HouseLine, MagnifyingGlass, CrosshairSimple, X, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { geocode, compass } from "../lib/proximity";
import { distMi, bearingDeg } from "../lib/followedFires";
import SponsorRotator from "./SponsorRotator";

const PIN_KEY = "firewatch:home_pin";

export function loadHomePin() {
  try {
    return JSON.parse(localStorage.getItem(PIN_KEY)) || null;
  } catch {
    return null;
  }
}

const angDiff = (a, b) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

export const YourLocationPanel = ({ fireLat, fireLon, fireName, spread, pin, onPinChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const setPin = (p) => {
    localStorage.setItem(PIN_KEY, JSON.stringify(p));
    setResults([]);
    setQuery("");
    onPinChange(p);
  };

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim() || busy) return;
    setBusy(true);
    try {
      const r = await geocode(query.trim());
      if (!r.length) toast.error("Address not found — try adding the town or ZIP.");
      setResults(r.slice(0, 3));
    } catch {
      toast.error("Address lookup failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const useDevice = () => {
    if (!navigator.geolocation) return toast.error("Location not available in this browser.");
    navigator.geolocation.getCurrentPosition(
      (p) => setPin({ lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" }),
      () => toast.error("Location permission denied."),
      { timeout: 8000 },
    );
  };

  let info = null;
  if (pin && fireLat != null) {
    const d = distMi(pin.lat, pin.lon, fireLat, fireLon);
    const dirFromPin = compass(bearingDeg(pin.lat, pin.lon, fireLat, fireLon));
    const dir = spread?.direction;
    let threat = null;
    if (dir?.moving && dir.bearing_deg != null) {
      const towardPin = bearingDeg(fireLat, fireLon, pin.lat, pin.lon);
      const diff = angDiff(dir.bearing_deg, towardPin);
      if (diff <= 45) {
        threat = { level: "toward", text: `Satellite data shows this fire spreading ${dir.compass} — roughly TOWARD your pin's direction. Stay alert and watch for evacuation notices.` };
      } else {
        threat = { level: "away", text: `Satellite data shows this fire spreading ${dir.compass} — not toward your pin right now. Conditions can change quickly.` };
      }
    } else if (spread?.available) {
      threat = { level: "hold", text: "No significant spread movement detected in recent satellite passes." };
    }
    info = { d, dirFromPin, threat };
  }

  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4" data-testid="your-location-panel">
      <div className="flex items-center gap-2 mb-3">
        <HouseLine size={14} weight="duotone" className="text-tac-primary" />
        <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
          Is Your Place in the Line of Fire?
        </span>
      </div>

      {!pin && (
        <>
          <form onSubmit={search} className="flex gap-2">
            <input
              data-testid="home-pin-address-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Address + town (e.g. 911 Aeneas Valley Rd, Tonasket)…"
              className="flex-1 bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-sm text-tac-text placeholder:text-tac-faint outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={busy || !query.trim()}
              data-testid="home-pin-search-btn"
              className="px-3 py-2 bg-tac-primary text-white rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-tac-primary/90 disabled:opacity-40"
            >
              <MagnifyingGlass size={14} weight="bold" />
            </button>
          </form>
          {results.length > 0 && (
            <div className="mt-2 border border-tac-border divide-y divide-tac-border/60">
              {results.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`home-pin-result-${i}`}
                  onClick={() =>
                    setPin({
                      lat: r.lat,
                      lon: r.lon,
                      label: r.display_name.startsWith("Coordinates")
                        ? r.display_name.replace("Coordinates ", "")
                        : r.display_name.split(",")[0],
                    })
                  }
                  className="block w-full text-left px-3 py-2 text-[13px] text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors"
                >
                  {r.display_name}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={useDevice}
            data-testid="home-pin-use-device-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-tac-info hover:text-tac-text transition-colors"
          >
            <CrosshairSimple size={12} weight="bold" /> Use my current location
          </button>
          <div className="text-[11px] font-mono-tac text-tac-faint mt-2">
            Set a pin for your home, cabin, or workplace — we'll show it on the map with the distance to this fire.
            Saved on your device only.
          </div>
        </>
      )}

      {pin && info && (
        <div data-testid="home-pin-info">
          <div className="text-sm text-tac-text">
            <span className="font-bold text-tac-primary">{pin.label}</span> is{" "}
            <span className="font-black tabular">{info.d.toFixed(1)} mi</span> from {fireName || "this fire"} —
            the fire sits <span className="font-bold">{info.dirFromPin}</span> of your pin.
          </div>
          {info.threat && (
            <div
              data-testid="home-pin-threat"
              className={`mt-2 flex items-start gap-2 text-[13px] p-2 border rounded-sm ${
                info.threat.level === "toward"
                  ? "border-tac-danger/60 bg-tac-danger/10 text-tac-danger"
                  : "border-tac-border bg-tac-surface/60 text-tac-dim"
              }`}
            >
              {info.threat.level === "toward" ? (
                <Warning size={15} weight="fill" className="shrink-0 mt-0.5" />
              ) : (
                <CheckCircle size={15} weight="fill" className="shrink-0 mt-0.5 text-tac-safe" />
              )}
              <span>{info.threat.text}</span>
            </div>
          )}
          <div className="text-[11px] font-mono-tac text-tac-faint mt-2">
            Informational only — always follow official evacuation orders from your Sheriff / EM.
          </div>
          <button
            type="button"
            data-testid="home-pin-clear-btn"
            onClick={() => { localStorage.removeItem(PIN_KEY); onPinChange(null); }}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-tac-faint hover:text-tac-danger transition-colors"
          >
            <X size={11} weight="bold" /> Clear pin
          </button>
        </div>
      )}

      <SponsorRotator variant="inline" />
    </div>
  );
};

export default YourLocationPanel;
